/**
 * 알림 — 확인 모달(showConfirm) · 토스트(showToast). **GRK_admin `contexts/AlertContext.tsx` 에서 옮겼다**(2026-10-16) —
 * 두 앱이 같은 확인창을 쓴다. 고칠 때는 admin 것도 같이 본다(코드 공유 대신 같은 모양으로 옮긴다).
 */
import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

// Toast 타입
interface Toast {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

// Confirm 상태
interface ConfirmState {
  isOpen: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

// Context 타입
interface AlertContextType {
  showToast: (message: string, type?: Toast['type']) => void;
  showConfirm: (message: string) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextType | null>(null);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within AlertProvider');
  }
  return context;
};

// Toast 컴포넌트
const ToastItem: React.FC<{ toast: Toast; onRemove: (id: number) => void }> = ({ toast, onRemove }) => {
  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500',
  }[toast.type];

  const icon = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  }[toast.type];

  React.useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), 3000);
    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  return (
    <div
      className={`${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[280px] animate-slide-in`}
    >
      <span className="text-lg">{icon}</span>
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={() => onRemove(toast.id)}
        className="text-white/80 hover:text-white"
      >
        ✕
      </button>
    </div>
  );
};

// Confirm 모달 컴포넌트
const ConfirmModal: React.FC<ConfirmState> = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-[400px] overflow-hidden animate-fade-in">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 text-xl">?</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">확인</h3>
              <p className="text-gray-600 whitespace-pre-line">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex border-t">
          <button
            onClick={onCancel}
            className="flex-1 py-3 text-gray-600 hover:bg-gray-50 font-medium transition-colors"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 text-white bg-blue-600 hover:bg-blue-700 font-medium transition-colors"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

// Provider
export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState>({
    isOpen: false,
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });

  // admin 원본은 `let toastId` — 린트 경고(exhaustive-deps)라 여기서는 ref 로 둔다(project 는 경고 0 을 지킨다)
  const toastId = useRef(0);

  const showToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = Date.now() + toastId.current++;
    setToasts((prev) => [...prev, { id, type, message }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirm({
        isOpen: true,
        message,
        onConfirm: () => {
          setConfirm((prev) => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirm((prev) => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <AlertContext.Provider value={{ showToast, showConfirm }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal {...confirm} />
    </AlertContext.Provider>
  );
};
